import { validateLocaleSet } from '@modules/shared-locales';
import type {
  ProjectLocaleConfig,
  ProjectLocaleGlossary,
  ProjectLocaleProfile,
  ProjectLocaleProfileContext,
  ProjectLocaleResourceStatus,
} from '../types/localeConfig.types';

export const MAX_LOCALE_PROFILE_BYTES = 64_000;
export const MAX_LOCALE_GLOSSARY_BYTES = 300_000;
export const MAX_LOCALE_GLOSSARY_ENTRIES = 500;
export const MAX_LOCALE_CONTEXT_LIST_ITEMS = 200;
export const MAX_LOCALE_RESOURCE_ID_CHARS = 128;

const RESOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;

export class LocaleAuthoringError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'LocaleAuthoringError';
  }
}

export function normalizeLocaleResourceId(value: unknown, field = 'id'): string {
  const id = requiredString(value, field, MAX_LOCALE_RESOURCE_ID_CHARS);
  if (!RESOURCE_ID_PATTERN.test(id)) {
    throw invalid('invalid_resource_id', `${field} must contain only letters, numbers, underscores, or hyphens.`);
  }
  return id;
}

export function normalizeProjectLocaleProfile(
  value: unknown,
  projectId: string,
  projectConfig: ProjectLocaleConfig,
): ProjectLocaleProfile {
  const record = requiredRecord(value, 'localization profile');
  assertOnlyKeys(record, 'localization profile', [
    'id', 'projectId', 'version', 'status', 'sourceLocale', 'targetLocales',
    'context', 'glossaryRef', 'createdAt', 'updatedAt',
  ]);
  const normalized = stripUndefined({
    id: normalizeLocaleResourceId(record.id),
    projectId: requiredProjectId(record.projectId, projectId),
    version: positiveVersion(record.version),
    status: normalizeStatus(record.status),
    ...normalizeLocaleScope(record.sourceLocale, record.targetLocales, projectConfig),
    context: normalizeLocaleProfileContext(record.context),
    glossaryRef: optionalResourceId(record.glossaryRef, 'glossaryRef'),
    createdAt: requiredIsoDate(record.createdAt, 'createdAt'),
    updatedAt: requiredIsoDate(record.updatedAt, 'updatedAt'),
  }) as ProjectLocaleProfile;
  assertByteLimit(normalized, MAX_LOCALE_PROFILE_BYTES, 'profile_too_large');
  return normalized;
}

export function normalizeProjectLocaleGlossary(
  value: unknown,
  projectId: string,
  projectConfig: ProjectLocaleConfig,
): ProjectLocaleGlossary {
  const record = requiredRecord(value, 'localization glossary');
  assertOnlyKeys(record, 'localization glossary', [
    'id', 'projectId', 'version', 'status', 'sourceLocale', 'targetLocales',
    'entries', 'createdAt', 'updatedAt',
  ]);
  const normalized = {
    id: normalizeLocaleResourceId(record.id),
    projectId: requiredProjectId(record.projectId, projectId),
    version: positiveVersion(record.version),
    status: normalizeStatus(record.status),
    ...normalizeLocaleScope(record.sourceLocale, record.targetLocales, projectConfig),
    entries: normalizeLocaleGlossaryEntries(record.entries),
    createdAt: requiredIsoDate(record.createdAt, 'createdAt'),
    updatedAt: requiredIsoDate(record.updatedAt, 'updatedAt'),
  } satisfies ProjectLocaleGlossary;
  assertByteLimit(normalized, MAX_LOCALE_GLOSSARY_BYTES, 'glossary_too_large');
  return normalized;
}

export function normalizeLocaleProfileContext(value: unknown): ProjectLocaleProfileContext {
  const record = requiredRecord(value ?? {}, 'profile context');
  assertOnlyKeys(record, 'profile context', [
    'targetMarket', 'tone', 'marketDirection', 'creativeContext', 'claims', 'protectedTokens',
  ]);
  const creativeContext = record.creativeContext === undefined
    ? undefined
    : normalizeCreativeContext(record.creativeContext);
  return stripUndefined({
    targetMarket: optionalString(record.targetMarket, 'targetMarket', 2_000),
    tone: optionalString(record.tone, 'tone', 2_000),
    marketDirection: optionalString(record.marketDirection, 'marketDirection', 4_000),
    creativeContext,
    claims: normalizeStringList(record.claims, 'claims', 2_000),
    protectedTokens: normalizeStringList(record.protectedTokens, 'protectedTokens', 500),
  });
}

export function normalizeLocaleGlossaryEntries(value: unknown): Record<string, string> {
  const record = requiredRecord(value, 'glossary entries');
  const rawEntries = Object.entries(record);
  if (rawEntries.length === 0) {
    throw invalid('glossary_empty', 'A glossary must contain at least one entry.');
  }
  if (rawEntries.length > MAX_LOCALE_GLOSSARY_ENTRIES) {
    throw invalid('glossary_limit_exceeded', `A glossary can contain at most ${MAX_LOCALE_GLOSSARY_ENTRIES} entries.`);
  }
  const normalizedSources = new Map<string, { source: string; target: string }>();
  for (const [rawSource, rawTarget] of rawEntries) {
    const source = requiredString(rawSource, 'glossary source', 500);
    const target = requiredString(rawTarget, `glossary target for ${source}`, 500);
    const key = normalizedTerm(source);
    const previous = normalizedSources.get(key);
    if (previous) {
      const conflicts = normalizedTerm(previous.target) !== normalizedTerm(target);
      throw invalid(
        conflicts ? 'glossary_term_conflict' : 'glossary_term_duplicate',
        conflicts
          ? `Glossary term ${source} conflicts with ${previous.source}.`
          : `Glossary term ${source} duplicates ${previous.source}.`,
      );
    }
    normalizedSources.set(key, { source, target });
  }
  return Object.fromEntries(Array.from(normalizedSources.values())
    .sort((left, right) => left.source.localeCompare(right.source))
    .map(({ source, target }) => [source, target]));
}

export function assertProfileGlossaryCompatibility(
  profile: Pick<ProjectLocaleProfile, 'sourceLocale' | 'targetLocales'>,
  glossary: ProjectLocaleGlossary,
): void {
  if (glossary.status !== 'active') {
    throw invalid('glossary_inactive', `Glossary ${glossary.id} is archived.`);
  }
  if (profile.sourceLocale !== glossary.sourceLocale) {
    throw invalid('glossary_locale_mismatch', `Glossary ${glossary.id} uses a different source locale.`);
  }
  const available = new Set(glossary.targetLocales);
  const missing = profile.targetLocales.filter((locale) => !available.has(locale));
  if (missing.length) {
    throw invalid('glossary_locale_mismatch', `Glossary ${glossary.id} does not support: ${missing.join(', ')}.`);
  }
}

export function assertProjectLocaleConfigForAuthoring(value: unknown): ProjectLocaleConfig {
  const record = requiredRecord(value, 'project locale configuration');
  const locales = Array.isArray(record.locales)
    ? record.locales.map((entry, index) => {
      const locale = requiredRecord(entry, `locales[${index}]`);
      return {
        code: requiredString(locale.code, `locales[${index}].code`, 128),
        label: requiredString(locale.label, `locales[${index}].label`, 256),
      };
    })
    : [];
  const defaultLocale = requiredString(record.defaultLocale, 'defaultLocale', 128);
  const validation = validateLocaleSet({
    defaultLanguage: defaultLocale,
    languages: locales.map((locale) => locale.code),
    projectLocales: locales.map((locale) => locale.code),
  });
  if (!validation.ok) {
    throw invalid('invalid_project_locale_config', 'Project locale configuration is invalid for authored localization resources.');
  }
  return {
    version: 1,
    locales,
    defaultLocale,
    updatedAt: typeof record.updatedAt === 'string' ? record.updatedAt : '',
  };
}

function normalizeLocaleScope(
  sourceValue: unknown,
  targetValue: unknown,
  config: ProjectLocaleConfig,
): { sourceLocale: string; targetLocales: string[] } {
  const configured = config.locales.map((locale) => locale.code);
  const sourceLocale = requiredString(sourceValue, 'sourceLocale', 128);
  const targets = Array.isArray(targetValue)
    ? targetValue.map((target, index) => requiredString(target, `targetLocales[${index}]`, 128))
    : [];
  if (targets.length === 0 || targets.length > 50) {
    throw invalid('invalid_target_locales', 'targetLocales must contain between 1 and 50 locales.');
  }
  const validation = validateLocaleSet({
    defaultLanguage: sourceLocale,
    languages: [sourceLocale, ...targets],
    projectLocales: configured,
  });
  if (!validation.ok) {
    throw invalid('locale_not_configured', 'Source and target locales must be configured project locales with no duplicates or aliases.');
  }
  if (targets.some((target) => target === sourceLocale)) {
    throw invalid('source_target_locale_conflict', 'The source locale cannot also be a target locale.');
  }
  return {
    sourceLocale: validation.value.defaultLanguage,
    targetLocales: validation.value.languages.slice(1).sort(),
  };
}

function normalizeCreativeContext(value: unknown): NonNullable<ProjectLocaleProfileContext['creativeContext']> {
  const record = requiredRecord(value, 'creativeContext');
  assertOnlyKeys(record, 'creativeContext', ['audience', 'offer', 'angle', 'brand', 'destinationSummary']);
  return stripUndefined({
    audience: optionalString(record.audience, 'creativeContext.audience', 8_000),
    offer: optionalString(record.offer, 'creativeContext.offer', 8_000),
    angle: optionalString(record.angle, 'creativeContext.angle', 8_000),
    brand: optionalString(record.brand, 'creativeContext.brand', 8_000),
    destinationSummary: optionalString(record.destinationSummary, 'creativeContext.destinationSummary', 8_000),
  });
}

function normalizeStringList(value: unknown, field: string, maxChars: number): string[] | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > MAX_LOCALE_CONTEXT_LIST_ITEMS) {
    throw invalid('invalid_context_list', `${field} must contain at most ${MAX_LOCALE_CONTEXT_LIST_ITEMS} values.`);
  }
  const seen = new Set<string>();
  const result = value.map((entry, index) => {
    const normalized = requiredString(entry, `${field}[${index}]`, maxChars);
    const key = normalizedTerm(normalized);
    if (seen.has(key)) throw invalid('context_value_duplicate', `${field} contains duplicate value ${normalized}.`);
    seen.add(key);
    return normalized;
  }).sort();
  return result.length ? result : undefined;
}

function requiredProjectId(value: unknown, projectId: string): string {
  const stored = requiredString(value, 'projectId', 256);
  if (stored !== projectId) throw invalid('cross_project_resource', 'The localization resource belongs to another project.');
  return stored;
}

function normalizeStatus(value: unknown): ProjectLocaleResourceStatus {
  if (value !== 'active' && value !== 'archived') {
    throw invalid('invalid_resource_status', 'Localization resource status must be active or archived.');
  }
  return value;
}

function optionalResourceId(value: unknown, field: string): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return normalizeLocaleResourceId(value, field);
}

function positiveVersion(value: unknown): number {
  const version = Number(value);
  if (!Number.isSafeInteger(version) || version <= 0) {
    throw invalid('invalid_resource_version', 'Localization resource version must be a positive integer.');
  }
  return version;
}

function requiredIsoDate(value: unknown, field: string): string {
  const normalized = requiredString(value, field, 64);
  if (!Number.isFinite(Date.parse(normalized))) throw invalid('invalid_timestamp', `${field} must be an ISO date.`);
  return normalized;
}

function assertByteLimit(value: unknown, maximum: number, code: string): void {
  if (new TextEncoder().encode(JSON.stringify(value)).byteLength > maximum) {
    throw invalid(code, `Localization resource exceeds ${maximum} bytes.`);
  }
}

function requiredString(value: unknown, field: string, maxChars: number): string {
  if (typeof value !== 'string' || !value.trim()) throw invalid('invalid_string', `${field} is required.`);
  const normalized = value.trim();
  if (normalized.length > maxChars) throw invalid('string_too_long', `${field} exceeds ${maxChars} characters.`);
  return normalized;
}

function optionalString(value: unknown, field: string, maxChars: number): string | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  return requiredString(value, field, maxChars);
}

function requiredRecord(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw invalid('invalid_object', `${field} must be an object.`);
  }
  return value as Record<string, unknown>;
}

function assertOnlyKeys(record: Record<string, unknown>, field: string, allowed: string[]): void {
  const unknown = Object.keys(record).filter((key) => !allowed.includes(key));
  if (unknown.length) throw invalid('unknown_resource_field', `${field} contains unknown field(s): ${unknown.join(', ')}.`);
}

function normalizedTerm(value: string): string {
  return value.normalize('NFKC').trim().toLocaleLowerCase('en-US');
}

function stripUndefined<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

function invalid(code: string, message: string): LocaleAuthoringError {
  return new LocaleAuthoringError(code, message);
}
